import { DateTime } from 'luxon';

import {
  MailSequenceReferenceDateType,
  MailSequenceScheduledSendStatus,
  PatientNameDataFragment,
} from '../../../generated/graphql';

export const isProgramDateSet = (date: string | null | undefined): boolean => {
  if (date == null || String(date).trim() === '') return false;
  return DateTime.fromISO(String(date)).isValid;
};

export const toDateInputValue = (date: string | null | undefined): string => {
  if (!date) return '';
  const dt = DateTime.fromISO(date);
  return dt.isValid ? dt.toFormat('yyyy-MM-dd') : '';
};

export const formatProgramDate = (
  date: string | null | undefined,
): string | null => {
  if (!date) return null;
  const dt = DateTime.fromISO(date);
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : null;
};

export const patientDisplayName = (patient: PatientNameDataFragment): string =>
  `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() ||
  patient.name ||
  patient.email;

type AnchorDateField = 'programStartDate' | 'programEndDate';

export type AnchorDateSummary =
  | { kind: 'no_recipients'; settingFieldLabel: string }
  | {
      kind: 'none_set';
      label: string;
      settingFieldLabel: string;
      clientCount: number;
      programInstanceId?: string;
    }
  | {
      kind: 'single';
      label: string;
      settingFieldLabel: string;
      formattedDate: string;
      dateInputValue: string;
      programInstanceId: string;
    }
  | {
      kind: 'shared';
      label: string;
      settingFieldLabel: string;
      formattedDate: string;
      dateInputValue: string;
      clientCount: number;
    }
  | {
      kind: 'multiple';
      label: string;
      settingFieldLabel: string;
      uniqueDateCount: number;
      entries: Array<{
        name: string;
        programInstanceId: string;
        formattedDate: string | null;
      }>;
    };

export const anchorDateFieldForReferenceType = (
  referenceDateType: MailSequenceReferenceDateType,
): AnchorDateField | null => {
  if (referenceDateType === MailSequenceReferenceDateType.ProgramStartDate) {
    return 'programStartDate';
  }
  if (referenceDateType === MailSequenceReferenceDateType.ProgramEndDate) {
    return 'programEndDate';
  }
  return null;
};

export const summarizeAnchorDates = (
  recipients: PatientNameDataFragment[],
  referenceDateType: MailSequenceReferenceDateType,
): AnchorDateSummary | null => {
  const field = anchorDateFieldForReferenceType(referenceDateType);
  if (!field) return null;

  const settingFieldLabel =
    field === 'programStartDate' ? 'Program start date' : 'Program end date';
  const label = settingFieldLabel;

  if (recipients.length === 0) {
    return { kind: 'no_recipients', settingFieldLabel };
  }

  const entries = recipients.map((patient) => {
    const rawDate = patient[field];
    return {
      name: patientDisplayName(patient),
      programInstanceId: patient.programInstanceId,
      rawDate,
      formattedDate: formatProgramDate(rawDate),
      hasDate: isProgramDateSet(rawDate),
    };
  });

  const entriesWithDate = entries.filter((entry) => entry.hasDate);

  if (entriesWithDate.length === 0) {
    return {
      kind: 'none_set',
      label,
      settingFieldLabel,
      clientCount: recipients.length,
      programInstanceId:
        recipients.length === 1 ? recipients[0].programInstanceId : undefined,
    };
  }

  if (entriesWithDate.length < entries.length) {
    return {
      kind: 'multiple',
      label,
      settingFieldLabel,
      uniqueDateCount: new Set(
        entries.map((entry) => entry.formattedDate ?? '__unset__'),
      ).size,
      entries: entries.map((entry) => ({
        name: entry.name,
        programInstanceId: entry.programInstanceId,
        formattedDate: entry.formattedDate,
      })),
    };
  }

  const uniqueDates = Array.from(
    new Set(
      entriesWithDate
        .map((entry) => entry.formattedDate)
        .filter((d): d is string => Boolean(d)),
    ),
  );

  const firstDatedEntry = entriesWithDate[0];

  if (recipients.length === 1) {
    return {
      kind: 'single',
      label,
      settingFieldLabel,
      formattedDate: uniqueDates[0],
      dateInputValue: toDateInputValue(firstDatedEntry.rawDate),
      programInstanceId: firstDatedEntry.programInstanceId,
    };
  }

  if (uniqueDates.length === 1) {
    return {
      kind: 'shared',
      label,
      settingFieldLabel,
      formattedDate: uniqueDates[0],
      dateInputValue: toDateInputValue(firstDatedEntry.rawDate),
      clientCount: recipients.length,
    };
  }

  return {
    kind: 'multiple',
    label,
    settingFieldLabel,
    uniqueDateCount: uniqueDates.length,
    entries: entries.map((entry) => ({
      name: entry.name,
      programInstanceId: entry.programInstanceId,
      formattedDate: entry.formattedDate,
    })),
  };
};

export const getResolvedAnchorDateIso = (
  referenceDateType: MailSequenceReferenceDateType,
  customAnchorDate: string,
  anchorDateSummary: AnchorDateSummary | null,
): string | null => {
  if (referenceDateType === MailSequenceReferenceDateType.Custom) {
    return customAnchorDate || null;
  }
  if (
    anchorDateSummary?.kind === 'single' ||
    anchorDateSummary?.kind === 'shared'
  ) {
    return anchorDateSummary.dateInputValue || null;
  }
  return null;
};

export type MailScheduleSummaryParts = {
  /** Resolved scheduled date, e.g. "Jul 23, 2026" — null if unresolvable. */
  dateLabel: string | null;
  /** Relative phrasing, e.g. "30 days after program start date". */
  relativeLabel: string;
};

/** Structured variant of {@link formatMailScheduleSummary}. */
export const getMailScheduleSummaryParts = (
  offsetDays: number,
  anchorLabel: string,
  anchorDateIso: string | null,
): MailScheduleSummaryParts => {
  const anchorDateFormatted = anchorDateIso
    ? formatProgramDate(anchorDateIso)
    : null;

  if (offsetDays === 0) {
    return {
      dateLabel: null,
      relativeLabel: `on ${anchorDateFormatted ?? anchorLabel}`,
    };
  }

  const absDays = Math.abs(offsetDays);
  const dayLabel = absDays === 1 ? 'day' : 'days';
  const timingWord = offsetDays < 0 ? 'before' : 'after';
  const anchorReference = anchorDateFormatted ?? anchorLabel;

  let scheduledDateFormatted: string | null = null;
  if (anchorDateIso) {
    const dt = DateTime.fromISO(anchorDateIso);
    if (dt.isValid) {
      scheduledDateFormatted = formatProgramDate(
        dt.plus({ days: offsetDays }).toISODate(),
      );
    }
  }

  return {
    dateLabel: scheduledDateFormatted,
    relativeLabel: `${absDays} ${dayLabel} ${timingWord} ${anchorReference}`,
  };
};

export const formatMailScheduleSummary = (
  offsetDays: number,
  anchorLabel: string,
  anchorDateIso: string | null,
): string => {
  const { dateLabel, relativeLabel } = getMailScheduleSummaryParts(
    offsetDays,
    anchorLabel,
    anchorDateIso,
  );
  return dateLabel ? `${dateLabel} (${relativeLabel})` : relativeLabel;
};

/**
 * Caption for a scheduled-send card, e.g. suffix "for Jul 23, 2026" with
 * secondary "30 days after program start date". Shared by the enrollment
 * details tab and the schedule preview modal.
 */
export const buildScheduledSendCaption = ({
  status,
  scheduledDateLabel,
  sentAtLabel,
  offsetDays,
  anchorLabel,
  anchorDateIso,
}: {
  status: MailSequenceScheduledSendStatus;
  scheduledDateLabel: string;
  sentAtLabel?: string | null;
  offsetDays: number;
  anchorLabel: string;
  anchorDateIso: string | null;
}): { suffix: string; secondary?: string } => {
  const { dateLabel, relativeLabel } = getMailScheduleSummaryParts(
    offsetDays,
    anchorLabel,
    anchorDateIso,
  );
  const secondary = dateLabel ? relativeLabel : undefined;

  switch (status) {
    case MailSequenceScheduledSendStatus.Sent:
      return { suffix: `on ${sentAtLabel ?? scheduledDateLabel}` };
    case MailSequenceScheduledSendStatus.Sending:
      return { suffix: 'now' };
    case MailSequenceScheduledSendStatus.Failed:
      return { suffix: `to send on ${scheduledDateLabel}`, secondary };
    case MailSequenceScheduledSendStatus.Skipped:
    case MailSequenceScheduledSendStatus.Cancelled:
      return { suffix: `sending on ${scheduledDateLabel}`, secondary };
    case MailSequenceScheduledSendStatus.Pending:
    default:
      return { suffix: `for ${scheduledDateLabel}`, secondary };
  }
};
